import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { filenameFromObjectKey, triggerBrowserDownload } from "utils/kycDocument";
import adminApiKeyAxiosInstance from "utils/adminApiKeyAxiosInstance";
import { getUrl } from "utils/getURL";
import { AdminAPIResource, CustomerSource, DocumentType, KycStatusGroup } from "constants/adminApi";
import { CustomerKYCFullDataResponse } from "types/admin";
import AdminPageHeader from "./components/AdminPageHeader";
import AdminCustomerInfoPanel from "./components/AdminCustomerInfoPanel";
import AdminDocumentViewerModal from "./components/AdminDocumentViewerModal";
import AdminKycDocumentsPanel from "./components/AdminKycDocumentsPanel";
import NameMatchExplanationDialog from "./components/NameMatchExplanationDialog";
import ApiLogTimelineEntry from "./components/ApiLogTimelineEntry";
import { prepareAuditLogEntries } from "./components/apiLogDisplayUtils";
import { formatDate } from "./components/adminKycVerificationUtils";
import {
  buildBankDetailTags,
  buildPanDetailTags,
} from "./components/kycDocumentDetailTags";
import { useKycDocumentLoader } from "./hooks/useKycDocumentLoader";

function VerifiedKycDetailPage() {
  const navigate = useNavigate();
  const { customerId = "", customerSource = "BRP" } = useParams<{
    customerId: string;
    customerSource: CustomerSource;
  }>();
  const [searchParams] = useSearchParams();
  const kycStatusGroup = (searchParams.get("kycStatusGroup") as KycStatusGroup | null) || "VERIFIED";

  const [detail, setDetail] = useState<CustomerKYCFullDataResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nameMatchExplainOpen, setNameMatchExplainOpen] = useState(false);

  const [docModalOpen, setDocModalOpen] = useState(false);
  const [activeDoc, setActiveDoc] = useState<{
    documentType: DocumentType;
    objectKey: string;
    title: string;
  } | null>(null);
  const [documentActionError, setDocumentActionError] = useState<string | null>(null);
  const [downloadingKey, setDownloadingKey] = useState<string | null>(null);

  const { getDocumentState, loadDocument } = useKycDocumentLoader(customerId, customerSource);

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const url = getUrl(
        AdminAPIResource.customerDetail,
        [{ variableName: "customerId", variableValue: customerId }],
        [
          { paramName: "customerSource", paramValue: customerSource },
          { paramName: "includeLogs", paramValue: "true" },
          { paramName: "kycStatusGroup", paramValue: kycStatusGroup },
        ]
      );
      const response = await adminApiKeyAxiosInstance.get<CustomerKYCFullDataResponse>(url);
      setDetail(response.data);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to load customer details";
      setError(message);
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [customerId, customerSource, kycStatusGroup]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const apiLogs = detail?.apiKycLogs ?? [];
  const displayAuditLogs = useMemo(() => prepareAuditLogEntries(apiLogs), [apiLogs]);
  const panDocKey = detail?.panDetails?.uploadedDocumentKey;
  const bankDocKey = detail?.bankDetails?.accountProofDocumentUrl;

  useEffect(() => {
    if (!detail) return;
    if (panDocKey) loadDocument("PAN_CARD_PROOF", panDocKey);
    if (bankDocKey) loadDocument("BANK_ACCOUNT_PROOF", bankDocKey);
  }, [detail, panDocKey, bankDocKey, loadDocument]);

  const openFullscreenPreview = useCallback(
    (documentType: DocumentType, objectKey: string, title: string) => {
      setDocumentActionError(null);
      setActiveDoc({ documentType, objectKey, title });
      setDocModalOpen(true);
      loadDocument(documentType, objectKey);
    },
    [loadDocument]
  );

  const downloadDocument = useCallback(
    async (documentType: DocumentType, objectKey?: string) => {
      if (!objectKey) {
        setDocumentActionError("No document available for this customer");
        return;
      }

      setDocumentActionError(null);
      setDownloadingKey(objectKey);
      try {
        const presignedUrl = await loadDocument(documentType, objectKey);
        if (!presignedUrl) {
          throw new Error("Failed to load document for download");
        }
        await triggerBrowserDownload(presignedUrl, filenameFromObjectKey(objectKey));
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : "Failed to download document";
        setDocumentActionError(message);
      } finally {
        setDownloadingKey(null);
      }
    },
    [loadDocument]
  );

  const activeDocState = activeDoc
    ? getDocumentState(activeDoc.documentType, activeDoc.objectKey)
    : { url: null, loading: false, error: null };

  const panName = detail?.panDetails?.fullName?.trim() || "";
  const bankName = detail?.bankDetails?.accountHolderName?.trim() || "";
  const namesAvailableForExplain = Boolean(panName && bankName);

  const documentPanelConfig = useMemo(
    () =>
      [
        {
          documentType: "PAN_CARD_PROOF" as DocumentType,
          title: "PAN Card Proof",
          subtitle: detail?.panDetails?.fullName || "Identity document",
          objectKey: panDocKey,
          detailTags: buildPanDetailTags(detail?.panDetails),
          state: getDocumentState("PAN_CARD_PROOF", panDocKey),
          downloading: Boolean(panDocKey && downloadingKey === panDocKey),
        },
        {
          documentType: "BANK_ACCOUNT_PROOF" as DocumentType,
          title: "Bank Account Proof",
          subtitle: detail?.bankDetails?.accountHolderName || "Account proof",
          objectKey: bankDocKey,
          detailTags: buildBankDetailTags(detail?.bankDetails),
          nameMatched: detail?.bankDetails?.nameMatched,
          onExplainNameMatch: () => setNameMatchExplainOpen(true),
          explainNameMatchDisabled: !namesAvailableForExplain,
          explainNameMatchDisabledReason: "Names unavailable for this customer",
          state: getDocumentState("BANK_ACCOUNT_PROOF", bankDocKey),
          downloading: Boolean(bankDocKey && downloadingKey === bankDocKey),
        },
      ] as const,
    [detail, panDocKey, bankDocKey, getDocumentState, downloadingKey, namesAvailableForExplain]
  );

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f5f7fa" }}>
      <AdminPageHeader
        title={`${customerId} (${customerSource})`}
        subtitle="Customer KYC details"
      />

      <Box sx={{ maxWidth: 1200, mx: "auto", p: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/verified-kyc-users")}
          sx={{ mb: 2 }}
        >
          Back to list
        </Button>

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={fetchDetail}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        {!loading && detail && (
          <Stack spacing={2.5}>
            {documentActionError && (
              <Alert severity="error" onClose={() => setDocumentActionError(null)}>
                {documentActionError}
              </Alert>
            )}

            <AdminCustomerInfoPanel detail={detail} />

            <AdminKycDocumentsPanel
              documents={[documentPanelConfig[0], documentPanelConfig[1]]}
              onExpand={(documentType, objectKey) => {
                const title =
                  documentType === "PAN_CARD_PROOF" ? "PAN Card Proof" : "Bank Account Proof";
                openFullscreenPreview(documentType, objectKey, title);
              }}
              onDownload={downloadDocument}
              onRetry={(documentType, objectKey) => {
                loadDocument(documentType, objectKey, true);
              }}
            />

            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" color="secondary.main" sx={{ mb: 0.5 }}>
                KYC Audit Log
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {detail.kycStartTime
                  ? `Showing logs from the current KYC window starting ${formatDate(detail.kycStartTime)}`
                  : "Showing verification logs for this customer"}
                {displayAuditLogs.length > 0 && ` (${displayAuditLogs.length} entries)`}. PAN
                compliance is shown as a pass/fail badge on the PAN Verification row. Expand an
                entry to see readable request and response details.
              </Typography>
              {displayAuditLogs.length > 0 ? (
                <Stack spacing={1}>
                  {displayAuditLogs.map((entry, index) => (
                    <ApiLogTimelineEntry
                      key={`${entry.requestType}-${entry.createdOn ?? index}`}
                      entry={entry}
                      getDocumentState={getDocumentState}
                      loadDocument={loadDocument}
                      onPreviewDocument={openFullscreenPreview}
                      onDownloadDocument={downloadDocument}
                    />
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  No audit log entries found for this customer&apos;s current KYC window.
                </Typography>
              )}
            </Paper>
          </Stack>
        )}
      </Box>

      <AdminDocumentViewerModal
        open={docModalOpen}
        onClose={() => setDocModalOpen(false)}
        imageSrc={activeDocState.url}
        objectKey={activeDoc?.objectKey}
        title={activeDoc?.title}
        loading={activeDocState.loading}
        error={activeDocState.error}
        onRetry={() => {
          if (activeDoc) {
            loadDocument(activeDoc.documentType, activeDoc.objectKey, true);
          }
        }}
        onDownload={
          activeDoc && activeDocState.url
            ? () => {
                triggerBrowserDownload(
                  activeDocState.url!,
                  filenameFromObjectKey(activeDoc.objectKey)
                ).catch((err: unknown) => {
                  const message =
                    err instanceof Error ? err.message : "Failed to download document";
                  setDocumentActionError(message);
                });
              }
            : undefined
        }
      />

      <NameMatchExplanationDialog
        open={nameMatchExplainOpen}
        onClose={() => setNameMatchExplainOpen(false)}
        panName={detail?.panDetails?.fullName}
        bankName={detail?.bankDetails?.accountHolderName}
        nameMatched={detail?.bankDetails?.nameMatched}
        userNameMatchScore={detail?.bankDetails?.userNameMatchScore}
      />
    </Box>
  );
}

export default VerifiedKycDetailPage;
